import { get, post, put, del } from "../../utils/axiosInstance";

const BASE_URL = "/rest/maintenance";

// ============================================================================
// Dashboard
// ============================================================================
export const fetchDashboard = () => {
  const url = `${BASE_URL}/machines/dashboard/`;
  return get(url);
};

export const fetchUpcomingMaintenance = (days = 30) => {
  const url = `${BASE_URL}/machines/upcoming_maintenance/`;
  const params = new URLSearchParams();
  if (days) params.append("days", days);
  return get(url, { params: Object.fromEntries(params) });
};

export const fetchOverdueMaintenance = () => {
  const url = `${BASE_URL}/machines/overdue/`;
  return get(url);
};

// ============================================================================
// Machine Types
// ============================================================================
export const fetchMachineTypes = (page = 1, isActive = null, search = null, pageSize = null) => {
  const url = `${BASE_URL}/machine-types/`;
  const params = { page };
  if (pageSize) params.page_size = pageSize;
  if (isActive !== null) params.is_active = isActive;
  if (search) params.search = search;
  return get(url, { params });
};

export const fetchMachineType = (id) => {
  const url = `${BASE_URL}/machine-types/${id}/`;
  return get(url);
};

export const createMachineType = (data) => {
  const url = `${BASE_URL}/machine-types/`;
  return post(url, data);
};

export const updateMachineType = (id, data) => {
  const url = `${BASE_URL}/machine-types/${id}/`;
  return put(url, data);
};

export const deleteMachineType = (id) => {
  const url = `${BASE_URL}/machine-types/${id}/`;
  return del(url);
};

// ============================================================================
// Facility Types
// ============================================================================
export const fetchFacilityTypes = (page = 1, isActive = null, search = null, pageSize = null) => {
  const url = `${BASE_URL}/facility-types/`;
  const params = { page };
  if (pageSize) params.page_size = pageSize;
  if (isActive !== null) params.is_active = isActive;
  if (search) params.search = search;
  return get(url, { params });
};

export const fetchFacilityType = (id) => {
  const url = `${BASE_URL}/facility-types/${id}/`;
  return get(url);
};

export const createFacilityType = (data) => {
  const url = `${BASE_URL}/facility-types/`;
  return post(url, data);
};

export const updateFacilityType = (id, data) => {
  const url = `${BASE_URL}/facility-types/${id}/`;
  return put(url, data);
};

export const deleteFacilityType = (id) => {
  const url = `${BASE_URL}/facility-types/${id}/`;
  return del(url);
};

// ============================================================================
// Locations
// ============================================================================
export const fetchLocations = (page = 1, isActive = null, search = null, pageSize = null) => {
  const url = `${BASE_URL}/locations/`;
  const params = { page };
  if (pageSize) params.page_size = pageSize;
  if (isActive !== null) params.is_active = isActive;
  if (search) params.search = search;
  return get(url, { params });
};

export const fetchLocation = (id) => {
  const url = `${BASE_URL}/locations/${id}/`;
  return get(url);
};

export const createLocation = (data) => {
  const url = `${BASE_URL}/locations/`;
  return post(url, data);
};

export const updateLocation = (id, data) => {
  const url = `${BASE_URL}/locations/${id}/`;
  return put(url, data);
};

export const deleteLocation = (id) => {
  const url = `${BASE_URL}/locations/${id}/`;
  return del(url);
};

// ============================================================================
// Service Providers
// ============================================================================
export const fetchServiceProviders = (page = 1, isActive = null, search = null, pageSize = null) => {
  const url = `${BASE_URL}/service-providers/`;
  const params = { page };
  if (pageSize) params.page_size = pageSize;
  if (isActive !== null) params.is_active = isActive;
  if (search) params.search = search;
  return get(url, { params });
};

export const fetchServiceProvider = (id) => {
  const url = `${BASE_URL}/service-providers/${id}/`;
  return get(url);
};

export const createServiceProvider = (data) => {
  const url = `${BASE_URL}/service-providers/`;
  return post(url, data);
};

export const updateServiceProvider = (id, data) => {
  const url = `${BASE_URL}/service-providers/${id}/`;
  return put(url, data);
};

export const deleteServiceProvider = (id) => {
  const url = `${BASE_URL}/service-providers/${id}/`;
  return del(url);
};

// ============================================================================
// Machines
// ============================================================================
export const fetchMachines = (page = 1, machineType = null, location = null, status = null, search = null, pageSize = null) => {
  const url = `${BASE_URL}/machines/`;
  const params = { page };
  if (pageSize) params.page_size = pageSize;
  if (machineType) params.machine_type = machineType;
  if (location) params.location = location;
  if (status) params.status = status;
  if (search) params.search = search;
  return get(url, { params });
};

export const fetchMachine = (id) => {
  const url = `${BASE_URL}/machines/${id}/`;
  return get(url);
};

export const createMachine = (data) => {
  const url = `${BASE_URL}/machines/`;
  if (data instanceof FormData) {
    return post(url, data, {
      headers: { "content-type": "multipart/form-data" },
    });
  }
  return post(url, data);
};

export const updateMachine = (id, data) => {
  const url = `${BASE_URL}/machines/${id}/`;
  if (data instanceof FormData) {
    return put(url, data, {
      headers: { "content-type": "multipart/form-data" },
    });
  }
  return put(url, data);
};

export const deleteMachine = (id) => {
  const url = `${BASE_URL}/machines/${id}/`;
  return del(url);
};

export const startMachineMaintenance = (id) => {
  const url = `${BASE_URL}/machines/${id}/start_maintenance/`;
  return post(url, {});
};

export const fetchMachineMaintenanceHistory = (id) => {
  const url = `${BASE_URL}/machines/${id}/maintenance_history/`;
  return get(url);
};

// ============================================================================
// Facilities
// ============================================================================
export const fetchFacilities = (page = 1, facilityType = null, location = null, status = null, search = null, pageSize = null) => {
  const url = `${BASE_URL}/facilities/`;
  const params = { page };
  if (pageSize) params.page_size = pageSize;
  if (facilityType) params.facility_type = facilityType;
  if (location) params.location = location;
  if (status) params.status = status;
  if (search) params.search = search;
  return get(url, { params });
};

export const fetchFacility = (id) => {
  const url = `${BASE_URL}/facilities/${id}/`;
  return get(url);
};

export const createFacility = (data) => {
  const url = `${BASE_URL}/facilities/`;
  if (data instanceof FormData) {
    return post(url, data, {
      headers: { "content-type": "multipart/form-data" },
    });
  }
  return post(url, data);
};

export const updateFacility = (id, data) => {
  const url = `${BASE_URL}/facilities/${id}/`;
  if (data instanceof FormData) {
    return put(url, data, {
      headers: { "content-type": "multipart/form-data" },
    });
  }
  return put(url, data);
};

export const deleteFacility = (id) => {
  const url = `${BASE_URL}/facilities/${id}/`;
  return del(url);
};

export const fetchFacilityTasks = (id, status = null) => {
  const url = `${BASE_URL}/facilities/${id}/tasks/`;
  const params = {};
  if (status) params.status = status;
  return get(url, { params });
};

// ============================================================================
// Task Templates
// ============================================================================
export const fetchTaskTemplates = (page = 1, taskType = null, targetType = null, frequency = null, isActive = null, search = null, pageSize = null) => {
  const url = `${BASE_URL}/task-templates/`;
  const params = { page };
  if (pageSize) params.page_size = pageSize;
  if (taskType) params.task_type = taskType;
  if (targetType) params.target_type = targetType;
  if (frequency) params.frequency = frequency;
  if (isActive !== null) params.is_active = isActive;
  if (search) params.search = search;
  return get(url, { params });
};

export const fetchTaskTemplate = (id) => {
  const url = `${BASE_URL}/task-templates/${id}/`;
  return get(url);
};

export const createTaskTemplate = (data) => {
  const url = `${BASE_URL}/task-templates/`;
  return post(url, data);
};

export const updateTaskTemplate = (id, data) => {
  const url = `${BASE_URL}/task-templates/${id}/`;
  return put(url, data);
};

export const deleteTaskTemplate = (id) => {
  const url = `${BASE_URL}/task-templates/${id}/`;
  return del(url);
};

// ============================================================================
// Task Assignments
// ============================================================================
export const fetchTaskAssignments = (page = 1, templateId = null, machineId = null, facilityId = null, isActive = null) => {
  const url = `${BASE_URL}/task-assignments/`;
  const params = { page };
  if (templateId) params.task_template = templateId;
  if (machineId) params.machine = machineId;
  if (facilityId) params.facility = facilityId;
  if (isActive !== null) params.is_active = isActive;
  return get(url, { params });
};

export const fetchTaskAssignment = (id) => {
  const url = `${BASE_URL}/task-assignments/${id}/`;
  return get(url);
};

export const createTaskAssignment = (data) => {
  const url = `${BASE_URL}/task-assignments/`;
  return post(url, data);
};

export const updateTaskAssignment = (id, data) => {
  const url = `${BASE_URL}/task-assignments/${id}/`;
  return put(url, data);
};

export const deleteTaskAssignment = (id) => {
  const url = `${BASE_URL}/task-assignments/${id}/`;
  return del(url);
};

export const generateAssignmentTasks = (id, daysAhead = 30) => {
  const url = `${BASE_URL}/task-assignments/${id}/generate_tasks/`;
  return post(url, { days_ahead: daysAhead });
};

export const generateAllTasks = (daysAhead = 30) => {
  const url = `${BASE_URL}/task-assignments/generate_all/`;
  return post(url, { days_ahead: daysAhead });
};

// ============================================================================
// Scheduled Tasks
// ============================================================================
export const fetchScheduledTasks = (status = null, taskType = null, targetType = null) => {
  const url = `${BASE_URL}/scheduled-tasks/`;
  const params = {};
  if (status) params.status = status;
  if (taskType) params.task_type = taskType;
  if (targetType) params.target_type = targetType;
  return get(url, { params });
};

export const fetchScheduledTask = (id) => {
  const url = `${BASE_URL}/scheduled-tasks/${id}/`;
  return get(url);
};

export const fetchTaskDashboard = () => {
  const url = `${BASE_URL}/scheduled-tasks/dashboard/`;
  return get(url);
};

export const fetchTodayTasks = () => {
  const url = `${BASE_URL}/scheduled-tasks/today/`;
  return get(url);
};

export const fetchOverdueTasks = () => {
  const url = `${BASE_URL}/scheduled-tasks/overdue/`;
  return get(url);
};

export const fetchUpcomingTasks = (days = 7) => {
  const url = `${BASE_URL}/scheduled-tasks/upcoming/`;
  const params = { days };
  return get(url, { params });
};

export const startScheduledTask = (id) => {
  const url = `${BASE_URL}/scheduled-tasks/${id}/start/`;
  return post(url, {});
};

export const completeScheduledTask = (id, data) => {
  const url = `${BASE_URL}/scheduled-tasks/${id}/complete/`;
  return post(url, data);
};

export const skipScheduledTask = (id, data) => {
  const url = `${BASE_URL}/scheduled-tasks/${id}/skip/`;
  return post(url, data);
};

export const addTaskPhoto = (id, photoData) => {
  const url = `${BASE_URL}/scheduled-tasks/${id}/add_photo/`;
  return post(url, photoData, {
    headers: { "content-type": "multipart/form-data" },
  });
};

// ============================================================================
// Maintenance Records
// ============================================================================
export const fetchMaintenanceRecords = (page = 1, machine = null, status = null, maintenanceType = null, serviceProvider = null, search = null) => {
  const url = `${BASE_URL}/records/`;
  const params = { page };
  if (machine) params.machine = machine;
  if (status) params.status = status;
  if (maintenanceType) params.maintenance_type = maintenanceType;
  if (serviceProvider) params.service_provider = serviceProvider;
  if (search) params.search = search;
  return get(url, { params });
};

export const fetchMaintenanceRecord = (id) => {
  const url = `${BASE_URL}/records/${id}/`;
  return get(url);
};

export const createMaintenanceRecord = (data) => {
  const url = `${BASE_URL}/records/`;
  return post(url, data);
};

export const updateMaintenanceRecord = (id, data) => {
  const url = `${BASE_URL}/records/${id}/`;
  return put(url, data);
};

export const deleteMaintenanceRecord = (id) => {
  const url = `${BASE_URL}/records/${id}/`;
  return del(url);
};

export const startMaintenanceRecord = (id) => {
  const url = `${BASE_URL}/records/${id}/start/`;
  return post(url, {});
};

export const completeMaintenanceRecord = (id, data) => {
  const url = `${BASE_URL}/records/${id}/complete/`;
  return post(url, data);
};

export const cancelMaintenanceRecord = (id) => {
  const url = `${BASE_URL}/records/${id}/cancel/`;
  return post(url, {});
};

export const recordServiceVisit = (data) => {
  const url = `${BASE_URL}/records/record_service_visit/`;
  return post(url, data);
};

// ============================================================================
// Maintenance Photos
// ============================================================================
export const fetchMaintenancePhotos = (page = 1, maintenanceRecord = null, photoType = null) => {
  const url = `${BASE_URL}/photos/`;
  const params = { page };
  if (maintenanceRecord) params.maintenance_record = maintenanceRecord;
  if (photoType) params.photo_type = photoType;
  return get(url, { params });
};

export const uploadMaintenancePhoto = (data) => {
  const url = `${BASE_URL}/photos/`;
  return post(url, data, {
    headers: { "content-type": "multipart/form-data" },
  });
};

export const deleteMaintenancePhoto = (id) => {
  const url = `${BASE_URL}/photos/${id}/`;
  return del(url);
};

// ============================================================================
// Notifications
// ============================================================================
export const fetchNotifications = (page = 1, machine = null, notificationType = null, isRead = null) => {
  const url = `${BASE_URL}/notifications/`;
  const params = { page };
  if (machine) params.machine = machine;
  if (notificationType) params.notification_type = notificationType;
  if (isRead !== null) params.is_read = isRead;
  return get(url, { params });
};

export const fetchUnreadCount = () => {
  const url = `${BASE_URL}/notifications/unread_count/`;
  return get(url);
};

export const markNotificationRead = (id) => {
  const url = `${BASE_URL}/notifications/${id}/mark_read/`;
  return post(url, {});
};

export const markAllNotificationsRead = () => {
  const url = `${BASE_URL}/notifications/mark_all_read/`;
  return post(url, {});
};
